/** The dates offered, from tomorrow. */
export const DELIVERY_DATE_CHIP_COUNT = 10;

export const DELIVERY_DAYS_FACET_KEY = 'attributes.delivery_days';

// The design's abbreviations, which are not Intl's: "Tues" and "Thur" rather than "Tue" and "Thu".
const WEEKDAYS = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];

const ordinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }

  return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
};

/**
 * "Tues 29th": the chips sit ten days from today, so the month adds nothing a shopper needs.
 * Dates are held at UTC midnight and read in UTC, so adding a day never lands on the wrong
 * side of a clock change.
 */
const formatDeliveryDate = (date: Date): string => {
  const day = date.getUTCDate();

  return `${WEEKDAYS[date.getUTCDay()]} ${day}${ordinalSuffix(day)}`;
};

/** The lead time in days of a delivery date option's value, `-3` for "3 days or less". */
export const getDeliveryDaysFromValue = (value: string): number | undefined => {
  const days = Number(value.match(/^-(\d+)$/)?.[1]);

  return days > 0 ? days : undefined;
};

/**
 * The UK calendar day `days` after `from`, which is the day `delivery_days <= days`
 * reaches, counted the way `getCalendarDaysUntilDate` counts. Held at UTC midnight,
 * so read it with the UTC getters.
 */
export const getDeliveryDate = (days: number, from: Date = new Date()): Date => {
  const date = new Date(from.toLocaleDateString('en-CA', { timeZone: 'Europe/London' }));
  date.setUTCDate(date.getUTCDate() + days);

  return date;
};

export const getDeliveryDateTitle = (days: number, from: Date = new Date()): string =>
  formatDeliveryDate(getDeliveryDate(days, from));

/** Titles for the `count` UK calendar days after `from`, starting tomorrow. */
export const getDeliveryChipDateTitles = (count: number, from: Date = new Date()): string[] =>
  Array.from({ length: count }, (_, index) => getDeliveryDateTitle(index + 1, from));

/** The two lines of a date chip, or undefined for a title that is not a date, such as an event's. */
export const splitDeliveryDateTitle = (
  title: string,
): { day: string; weekday: string } | undefined => {
  const match = title.match(/^(\w+) (\d{1,2}(?:st|nd|rd|th))$/);

  return match ? { day: match[2]!, weekday: match[1]! } : undefined;
};
